import UnitsDialog, { Scanner } from '../components/UnitsDialog';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLicense } from '../contexts/LicenseContext';
import { api } from '../lib/api';
import { getCurrentCompanyId } from '../lib/company';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import ImageUploader from '../components/ui/image-uploader';
import { Plus, Edit, Trash2, Download, Package, Upload, Percent, Tag, CheckSquare, Square, History, FolderTree, X, Wrench, List, LayoutGrid, Warehouse, ArrowRightLeft, MapPin, ScanBarcode, Smartphone, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { salesProFilename, downloadBlob } from '../lib/salespro';
import { catChipStyle, catBadgeStyle } from '../lib/categoryColors';

export default function Inventory() {
  const { user } = useAuth();
  const { license } = useLicense();
  const vFlags = license?.vertical_flags || {};
  const navigate = useNavigate();
  const isManager = ['master', 'superadmin', 'admin'].includes(user?.role_id);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catName, setCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [charRows, setCharRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ adjustment_type: 'percent_increase', value: '', label: '', reason: '' });
  const [warehouses, setWarehouses] = useState([]);
  const [whDialogOpen, setWhDialogOpen] = useState(false);
  const [whName, setWhName] = useState('');
  const [editingWh, setEditingWh] = useState(null);
  const [codeScanOpen, setCodeScanOpen] = useState(false);
  const [initialImeis, setInitialImeis] = useState([]); // IMEIs pre-cargados al crear producto nuevo (equipos)
  const [imeiScanOpen, setImeiScanOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveForm, setMoveForm] = useState({ mode: 'category', category_id: '', almacen: '' });
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    product_type: '',
    model: '',
    item_type: 'bien',
    category_id: '',
    price: 0,
    cost: 0,
    stock: 0,
    marca: '',
    unidad: '',
    almacen: '',
    location: '',
    photos: [],
    characteristics: {},
    credit_plan: { down_payment: '', installments: '', frequency: '14', installment_amount: '' }
  });
  const [viewMode, setViewMode] = useState('grid');

  const loadProducts = async () => {
    try {
      const response = await api.getProducts({ company_id: getCurrentCompanyId() });
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.getCategories({ company_id: getCurrentCompanyId() });
      setCategories(res.data);
    } catch (error) {
      setCategories([]);
    }
  };

  const loadWarehouses = async () => {
    try {
      const res = await api.getWarehouses({ company_id: getCurrentCompanyId() });
      setWarehouses(res.data);
    } catch (error) {
      setWarehouses([]);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => { await loadProducts(); await loadCategories(); await loadWarehouses(); })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const catName_ = (id) => categories.find(c => c.category_id === id)?.name || null;

  const saveCategory = async () => {
    const name = catName.trim();
    if (!name) { toast.error('SalesPRO: Ingresa un nombre de categoría'); return; }
    const company_id = getCurrentCompanyId();
    if (!company_id) { toast.error('SalesPRO: Selecciona una empresa en el panel'); return; }
    try {
      if (editingCat) {
        await api.updateCategory(editingCat.category_id, { name, company_id });
        toast.success('SalesPRO: Categoría actualizada');
      } else {
        await api.createCategory({ name, company_id });
        toast.success('SalesPRO: Categoría creada');
      }
      setCatName('');
      setEditingCat(null);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al guardar categoría');
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`SalesPRO: ¿Eliminar la categoría "${cat.name}"? Los productos quedarán sin categoría.`)) return;
    try {
      await api.deleteCategory(cat.category_id);
      toast.success('SalesPRO: Categoría eliminada');
      if (filterCategory === cat.category_id) setFilterCategory('');
      loadCategories();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al eliminar categoría');
    }
  };

  const saveWarehouse = async () => {
    const name = whName.trim();
    if (!name) { toast.error('SalesPRO: Ingresa un nombre de almacén'); return; }
    const company_id = getCurrentCompanyId();
    if (!company_id) { toast.error('SalesPRO: Selecciona una empresa en el panel'); return; }
    try {
      if (editingWh) {
        await api.updateWarehouse(editingWh.warehouse_id, { name, company_id });
        toast.success('SalesPRO: Almacén actualizado');
      } else {
        await api.createWarehouse({ name, company_id });
        toast.success('SalesPRO: Almacén creado');
      }
      setWhName(''); setEditingWh(null);
      loadWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al guardar almacén');
    }
  };

  const deleteWarehouse = async (wh) => {
    if (!window.confirm(`SalesPRO: ¿Eliminar el almacén "${wh.name}"? Sus productos se moverán al Almacén General.`)) return;
    try {
      const res = await api.deleteWarehouse(wh.warehouse_id);
      toast.success(res.data?.message || 'SalesPRO: Almacén eliminado');
      loadWarehouses(); loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al eliminar almacén');
    }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`SalesPRO: ¿Eliminar ${selected.length} producto(s) seleccionado(s)? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await api.bulkDeleteProducts({ product_ids: selected, company_id: getCurrentCompanyId() });
      toast.success(res.data?.message || 'SalesPRO: Productos eliminados');
      setSelected([]); loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al eliminar');
    }
  };

  const applyMove = async () => {
    if (selected.length === 0) return;
    const payload = { product_ids: selected, company_id: getCurrentCompanyId() };
    if (moveForm.mode === 'category') {
      payload.set_category = true;
      payload.category_id = moveForm.category_id || null;
    } else {
      if (!moveForm.almacen) { toast.error('SalesPRO: Selecciona un almacén destino'); return; }
      payload.almacen = moveForm.almacen;
    }
    try {
      const res = await api.bulkMoveProducts(payload);
      toast.success(res.data?.message || 'SalesPRO: Productos movidos');
      setMoveOpen(false); setSelected([]); setMoveForm({ mode: 'category', category_id: '', almacen: '' });
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al mover productos');
    }
  };

  const charsToRows = (obj) => Object.entries(obj || {}).map(([k, v]) => ({ key: k, value: String(v) }));
  const rowsToChars = (rows) => Object.fromEntries(
    rows.filter(r => r.key && r.key.trim()).map(r => [r.key.trim(), r.value])
  );

  const openCreate = () => {
    setEditingProduct(null);
    setFormData({ code: '', name: '', product_type: '', model: '', item_type: 'bien', category_id: '', price: 0, cost: 0, stock: 0, marca: '', unidad: '', almacen: '', location: '', photos: [], characteristics: {}, credit_plan: { down_payment: '', installments: '', frequency: '14', installment_amount: '' } });
    setCharRows([]);
    setInitialImeis([]);
    setCodeScanOpen(false);
    setImeiScanOpen(false);
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      code: product.code || '',
      name: product.name || '', product_type: product.product_type || '', model: product.model || '',
      item_type: product.item_type || 'bien', category_id: product.category_id || '',
      price: product.price || 0, cost: product.cost || 0, stock: product.stock || 0,
      marca: product.marca || '', unidad: product.unidad || '', almacen: product.almacen || '', location: product.location || '',
      photos: product.photos || [], characteristics: product.characteristics || {},
      credit_plan: product.credit_plan || { down_payment: '', installments: '', frequency: '14', installment_amount: '' }
    });
    setCharRows(charsToRows(product.characteristics));
    setDetailProduct(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        company_id: getCurrentCompanyId() || undefined,
        category_id: formData.category_id || null,
        characteristics: rowsToChars(charRows),
        credit_plan: (() => {
          const inst = parseInt(formData.credit_plan?.installments) || 0;
          if (!inst) return null;
          const price = parseFloat(formData.price) || 0;
          const rawDown = parseFloat(formData.credit_plan.down_payment) || 0;
          // Validaciones: precio > 0 y la inicial no puede ser mayor al precio
          const down = Math.min(Math.max(0, rawDown), price);
          const financed = Math.max(0, price - down);
          const amt = Math.round((financed / inst) * 100) / 100;
          return { down_payment: down, installments: inst, frequency: formData.credit_plan.frequency || '14', installment_amount: amt };
        })(),
        photos: Array.isArray(formData.photos) ? formData.photos : []
      };
      if (editingProduct) {
        await api.updateProduct(editingProduct.product_id, payload);
        toast.success('SalesPRO: Producto actualizado');
      } else {
        // Si vienen IMEIs, el stock lo marca la cantidad de IMEIs
        if (initialImeis.length > 0) {
          payload.stock = initialImeis.length;
        }
        const resp = await api.createProduct(payload);
        const newPid = resp?.data?.product_id || resp?.data?.id;
        if (initialImeis.length > 0 && newPid) {
          try {
            const r = await api.addUnits(newPid, initialImeis);
            toast.success(`SalesPRO: Producto creado · ${r.data.added || initialImeis.length} IMEI(s) registrados`);
          } catch (err) {
            toast.error(`Producto creado pero no se pudieron agregar los IMEIs: ${err.response?.data?.detail || err.message}`);
          }
        } else {
          toast.success('SalesPRO: Producto creado');
        }
      }
      setDialogOpen(false);
      setEditingProduct(null);
      loadProducts();
      setFormData({ code: '', name: '', product_type: '', model: '', item_type: 'bien', category_id: '', price: 0, cost: 0, stock: 0, marca: '', unidad: '', almacen: '', location: '', photos: [], characteristics: {} });
      setCharRows([]);
      setInitialImeis([]);
      setCodeScanOpen(false);
      setImeiScanOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al guardar producto');
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`¿Eliminar "${product.name}"? Se quitará del inventario. Las facturas, notas y presupuestos previos se conservan intactos.`)) return;
    try {
      await api.deleteProduct(product.product_id);
      toast.success('SalesPRO: Producto eliminado');
      setDetailProduct(null);
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: No se pudo eliminar el producto');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.exportProductsReport(getCurrentCompanyId() || undefined);
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      downloadBlob(blob, salesProFilename('Inventario', 'xlsx'));
      toast.success('SalesPRO: Reporte exportado');
    } catch (error) {
      toast.error('SalesPRO: Error al exportar');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.downloadProductTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      downloadBlob(blob, salesProFilename('Plantilla_Productos', 'xlsx'));
      toast.success('SalesPRO: Plantilla descargada');
    } catch (error) {
      toast.error('SalesPRO: Error al descargar plantilla');
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === products.length) setSelected([]);
    else setSelected(products.map(p => p.product_id));
  };

  const previewLabel = () => {
    const v = parseFloat(bulkForm.value);
    if (isNaN(v)) return '';
    switch (bulkForm.adjustment_type) {
      case 'percent_increase': return `+${v}% sobre el precio actual`;
      case 'percent_decrease': return `-${v}% sobre el precio actual`;
      case 'fixed_increase': return `+$${v.toFixed(2)} a cada producto`;
      case 'fixed_decrease': return `-$${v.toFixed(2)} a cada producto`;
      case 'set_price': return `Fijar precio en $${v.toFixed(2)}`;
      default: return '';
    }
  };

  const applyBulk = async () => {
    const v = parseFloat(bulkForm.value);
    if (isNaN(v) || v < 0) { toast.error('SalesPRO: Ingresa un valor numérico válido'); return; }
    if (selected.length === 0) { toast.error('SalesPRO: Selecciona al menos un producto'); return; }
    const company_id = getCurrentCompanyId()
      || (products.find(p => selected.includes(p.product_id))?.company_id)
      || user?.company_id;
    if (!company_id) { toast.error('SalesPRO: No se pudo determinar la empresa. Selecciona una empresa en el panel.'); return; }
    try {
      const res = await api.applyBulkPricing({
        product_ids: selected,
        company_id,
        adjustment_type: bulkForm.adjustment_type,
        value: v,
        label: bulkForm.label || undefined,
        reason: bulkForm.reason || undefined,
      });
      toast.success(res.data.message);
      setBulkOpen(false);
      setSelected([]);
      setBulkForm({ adjustment_type: 'percent_increase', value: '', label: '', reason: '' });
      loadProducts();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'SalesPRO: Error al aplicar cambios');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;

    const company_id = getCurrentCompanyId() || undefined;
    const loadingToast = toast.loading('SalesPRO: Analizando archivo...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Paso 1: previsualizar (no inserta) para confirmar antes de cargar
      const res = await api.bulkUploadProductsExcel(formData, { company_id, commit: false });
      toast.dismiss(loadingToast);
      setBulkPreview({ ...res.data, file, company_id });
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al leer el archivo');
    }
  };

  const confirmBulkUpload = async () => {
    if (!bulkPreview) return;
    setBulkUploading(true);
    const loadingToast = toast.loading('SalesPRO: Cargando productos...');
    try {
      const formData = new FormData();
      formData.append('file', bulkPreview.file);
      const res = await api.bulkUploadProductsExcel(formData, { company_id: bulkPreview.company_id, commit: true });
      toast.dismiss(loadingToast);
      const { count, errors } = res.data;
      if (errors && errors.length > 0) {
        toast.warning(`SalesPRO: ${count} cargado(s), ${errors.length} con error.`);
      } else {
        toast.success(res.data.message || `SalesPRO: ${count} productos cargados`);
      }
      setBulkPreview(null);
      loadProducts();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al cargar archivo');
    } finally {
      setBulkUploading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="inventory-loading">Cargando...</div>;
  }

  const visibleProducts = products
    .filter(p => !filterCategory || p.category_id === filterCategory)
    .filter(p => {
      const q = searchTerm.trim().toLowerCase();
      if (!q) return true;
      return (p.name || '').toLowerCase().includes(q)
        || (p.code || '').toLowerCase().includes(q)
        || (p.model || '').toLowerCase().includes(q);
    })
    .slice()
    .sort((a, b) => {
      const ca = (categories.find(c => c.category_id === a.category_id)?.name || 'zzz').toLowerCase();
      const cb = (categories.find(c => c.category_id === b.category_id)?.name || 'zzz').toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
    });

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Inventario</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestiona tus productos</p>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap [&_button]:shrink-0" data-testid="inventory-actions">
          {user?.role_id === 'master' && (
            <Button onClick={() => navigate('/pricing-history')} variant="outline" size="sm" data-testid="pricing-history-button">
              <History size={16} className="mr-1.5" />
              Histórico
            </Button>
          )}
          {isManager && (
            <Button onClick={() => { setEditingCat(null); setCatName(''); setCatDialogOpen(true); }} variant="outline" size="sm" data-testid="manage-categories-button">
              <FolderTree size={16} className="mr-1.5" />
              Categorías
            </Button>
          )}
          {isManager && (
            <Button onClick={() => { setEditingWh(null); setWhName(''); setWhDialogOpen(true); }} variant="outline" size="sm" data-testid="manage-warehouses-button">
              <Warehouse size={16} className="mr-1.5" />
              Almacenes
            </Button>
          )}
          <Button onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')} variant="outline" size="sm" data-testid="toggle-view-mode">
            {viewMode === 'grid' ? <><List size={16} className="mr-1.5" /> Lista</> : <><LayoutGrid size={16} className="mr-1.5" /> Fotos</>}
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm" data-testid="export-products-button">
            <Download size={16} className="mr-1.5" />
            Exportar
          </Button>
          <Button onClick={handleDownloadTemplate} variant="outline" size="sm" data-testid="download-template-button">
            <Download size={16} className="mr-1.5" />
            Plantilla
          </Button>
          <label htmlFor="bulk-upload">
            <Button variant="outline" size="sm" className="cursor-pointer" asChild data-testid="bulk-upload-button">
              <span>
                <Upload size={16} className="mr-1.5" />
                Carga Masiva
              </span>
            </Button>
          </label>
          <input
            id="bulk-upload"
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleBulkUpload}
            data-testid="bulk-upload-input"
          />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <Button size="sm" onClick={openCreate} data-testid="add-product-button">
              <Plus size={16} className="mr-1.5" />
              Nuevo Producto
            </Button>
            <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="code">Código de producto</Label>
                  <div className="flex gap-2">
                    <Input
                      id="code"
                      data-testid="product-code-input"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      placeholder="Ej: SKU-0001"
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => setCodeScanOpen((v) => !v)} data-testid="product-code-scan-btn" title="Escanear código de barras">
                      <ScanBarcode size={14} className="mr-1" /> Escanear
                    </Button>
                  </div>
                  {codeScanOpen && (
                    <div className="mt-2 border rounded-md p-2 bg-[#F8FAFC]" data-testid="product-code-scan-panel">
                      <Scanner scanId="product-code-scanner" onCode={(c) => { setFormData((f) => ({ ...f, code: c })); setCodeScanOpen(false); toast.success(`Código leído: ${c}`); }} onClose={() => setCodeScanOpen(false)} />
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="name">Nombre</Label>
                  <Input
                    id="name"
                    data-testid="product-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="type">Tipo</Label>
                  <Input
                    id="type"
                    data-testid="product-type-input"
                    value={formData.product_type}
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="model">Modelo</Label>
                  <Input
                    id="model"
                    data-testid="product-model-input"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="item_type">Tipo de ítem</Label>
                    <select
                      id="item_type"
                      value={formData.item_type}
                      onChange={(e) => setFormData({ ...formData, item_type: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10"
                      data-testid="product-item-type"
                    >
                      <option value="bien">Bien (descuenta stock)</option>
                      <option value="servicio">Servicio (no descuenta stock)</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="category">Categoría</Label>
                    <select
                      id="category"
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10"
                      data-testid="product-category-select"
                    >
                      <option value="">— Sin categoría —</option>
                      {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Precio</Label>
                    <Input
                      id="price"
                      data-testid="product-price-input"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="stock">Stock</Label>
                    <Input
                      id="stock"
                      data-testid="product-stock-input"
                      type="number"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>
                {/* IMEIs del equipo (solo rubros que usan IMEI, ej. electrónica) */}
                {!editingProduct && formData.item_type === 'bien' && vFlags.requires_imei !== false && (
                  <div className="rounded-lg border border-primary/30 bg-[#F0FBF7] p-3 space-y-2" data-testid="product-imei-section">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2"><Smartphone size={14} /> IMEIs del equipo (opcional)</Label>
                      <Button type="button" variant="outline" size="sm" onClick={() => setImeiScanOpen((v) => !v)} data-testid="product-imei-scan-btn">
                        <Camera size={14} className="mr-1" /> Escanear IMEI
                      </Button>
                    </div>
                    <p className="text-[11px] text-[#64748B]">Agrega los IMEIs de los celulares que entraron con este producto. Puedes escanear uno por uno o escribirlos. Si no cargas IMEIs ahora, podrás agregarlos luego desde el detalle del producto.</p>
                    {imeiScanOpen && (
                      <div className="border rounded-md p-2 bg-white">
                        <Scanner scanId="product-imei-scanner" onCode={(c) => {
                          const clean = (c || '').replace(/\D/g, '');
                          if (!clean) return;
                          if (initialImeis.includes(clean)) { toast.error(`IMEI ${clean} ya está en la lista`); return; }
                          setInitialImeis((prev) => [...prev, clean]);
                          toast.success(`IMEI agregado: ${clean}`);
                        }} onClose={() => setImeiScanOpen(false)} paused={false} />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input placeholder="IMEI manual (15 dígitos)" id="imei-manual-input"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const v = (e.currentTarget.value || '').replace(/\D/g, ''); if (!v) return; if (initialImeis.includes(v)) { toast.error('Ya existe'); return; } setInitialImeis((p) => [...p, v]); e.currentTarget.value = ''; } }}
                        data-testid="product-imei-manual-input" />
                      <Button type="button" variant="outline" size="sm" onClick={() => { const el = document.getElementById('imei-manual-input'); if (!el) return; const v = (el.value || '').replace(/\D/g, ''); if (!v) return; if (initialImeis.includes(v)) { toast.error('Ya existe'); return; } setInitialImeis((p) => [...p, v]); el.value = ''; }} data-testid="product-imei-add-btn">Agregar</Button>
                    </div>
                    {initialImeis.length > 0 && (
                      <div className="flex flex-wrap gap-1.5" data-testid="product-imei-list">
                        {initialImeis.map((im) => (
                          <span key={im} className="inline-flex items-center gap-1 text-[11px] bg-white border rounded-full px-2 py-0.5">
                            {im}
                            <button type="button" onClick={() => setInitialImeis((p) => p.filter((x) => x !== im))} className="text-red-600 hover:text-red-800"><X size={11} /></button>
                          </span>
                        ))}
                      </div>
                    )}
                    {initialImeis.length > 0 && formData.stock !== initialImeis.length && (
                      <p className="text-[11px] text-amber-700" data-testid="product-imei-warn">⚠ Tienes {initialImeis.length} IMEIs pero el stock dice {formData.stock || 0}. El stock se ajustará automáticamente a {initialImeis.length} al guardar.</p>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cost">Costo</Label>
                    <Input id="cost" data-testid="product-cost-input" type="number" step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <Label htmlFor="marca">Marca</Label>
                    <Input id="marca" data-testid="product-marca-input"
                      value={formData.marca}
                      onChange={(e) => setFormData({ ...formData, marca: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="unidad">Unidad</Label>
                    <Input id="unidad" data-testid="product-unidad-input" placeholder="UND, KG, CAJA"
                      value={formData.unidad}
                      onChange={(e) => setFormData({ ...formData, unidad: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="almacen">Almacén</Label>
                    <select id="almacen" data-testid="product-almacen-select"
                      value={formData.almacen}
                      onChange={(e) => setFormData({ ...formData, almacen: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10">
                      <option value="">Almacén General</option>
                      {warehouses.map(w => <option key={w.warehouse_id} value={w.name}>{w.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="location">Ubicación</Label>
                    <Input id="location" data-testid="product-location-input" placeholder="Ej: Pasillo 3 - Estante B"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
                  </div>
                </div>
                {vFlags.enable_credit_plans !== false && (
                <div className="rounded-lg border border-primary/30 bg-[#F0FBF7] p-3 space-y-2" data-testid="product-credit-plan">
                  <Label>Plan de crédito del equipo (se muestra en la vitrina y la pre-calificación)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Input type="number" placeholder="Inicial $" value={formData.credit_plan?.down_payment ?? ''} onChange={(e) => setFormData({ ...formData, credit_plan: { ...formData.credit_plan, down_payment: e.target.value } })} data-testid="product-plan-down" />
                    <Input type="number" placeholder="N° cuotas" value={formData.credit_plan?.installments ?? ''} onChange={(e) => setFormData({ ...formData, credit_plan: { ...formData.credit_plan, installments: e.target.value } })} data-testid="product-plan-installments" />
                    <select value={formData.credit_plan?.frequency || '14'} onChange={(e) => setFormData({ ...formData, credit_plan: { ...formData.credit_plan, frequency: e.target.value } })} className="px-2 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white" data-testid="product-plan-frequency">
                      <option value="7">Semanal</option><option value="14">Quincenal</option><option value="mensual">Mensual</option>
                    </select>
                  </div>
                  {formData.credit_plan?.installments > 0 && (() => {
                    const price = parseFloat(formData.price) || 0;
                    const rawDown = parseFloat(formData.credit_plan.down_payment) || 0;
                    const inst = parseInt(formData.credit_plan.installments) || 1;
                    const down = Math.min(Math.max(0, rawDown), price);
                    const amt = Math.max(0, (price - down) / inst);
                    const warn = price <= 0 ? 'Define primero el precio del producto.' : (rawDown > price ? 'La inicial no puede ser mayor al precio.' : '');
                    return (
                      <div className="text-xs" data-testid="product-plan-preview">
                        <p className={warn ? 'text-red-700 font-semibold' : 'text-[#022C22]'}>Cuota: <b>${amt.toFixed(2)}</b> × {inst}</p>
                        {warn && <p className="text-red-700">⚠ {warn}</p>}
                      </div>
                    );
                  })()}
                </div>
                )}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Ficha técnica / Características</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setCharRows([...charRows, { key: '', value: '' }])} data-testid="add-characteristic">
                      <Plus size={14} className="mr-1" /> Agregar
                    </Button>
                  </div>
                  {charRows.length === 0 && <p className="text-xs text-[#94A3B8]">Agrega atributos como Color, Material, Garantía, Potencia, etc.</p>}
                  {charRows.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`char-row-${idx}`}>
                      <Input className="col-span-5" placeholder="Atributo" value={row.key}
                        onChange={(e) => setCharRows(charRows.map((c, i) => i === idx ? { ...c, key: e.target.value } : c))} />
                      <Input className="col-span-6" placeholder="Valor" value={row.value}
                        onChange={(e) => setCharRows(charRows.map((c, i) => i === idx ? { ...c, value: e.target.value } : c))} />
                      <Button type="button" size="icon" variant="ghost" className="col-span-1"
                        onClick={() => setCharRows(charRows.filter((_, i) => i !== idx))}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div>
                  <ImageUploader
                    value={formData.photos}
                    onChange={(ids) => setFormData({ ...formData, photos: ids })}
                    multiple={true}
                    purpose="product"
                    label="Fotos del producto"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-product-button">
                  {editingProduct ? 'Guardar cambios' : 'Crear Producto'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isManager && products.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-white border border-[#E2E8F0] rounded-md px-3 py-2" data-testid="bulk-toolbar">
          <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-[#022C22] shrink-0" data-testid="select-all-products">
            {selected.length === products.length && products.length > 0 ? <CheckSquare size={18} className="text-primary" /> : <Square size={18} className="text-[#94A3B8]" />}
            <span>{selected.length > 0 ? `${selected.length} seleccionado(s)` : 'Seleccionar todo'}</span>
          </button>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button size="sm" variant="outline" disabled={selected.length === 0} onClick={() => setBulkOpen(true)} data-testid="bulk-actions-button">
              <Percent size={15} className="mr-1.5" /> Precios
            </Button>
            <Button size="sm" variant="outline" disabled={selected.length === 0} onClick={() => { setMoveForm({ mode: 'category', category_id: '', almacen: '' }); setMoveOpen(true); }} data-testid="bulk-move-button">
              <ArrowRightLeft size={15} className="mr-1.5" /> Mover
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={selected.length === 0} onClick={bulkDelete} data-testid="bulk-delete-button">
              <Trash2 size={15} className="mr-1.5" /> Borrar
            </Button>
            {selected.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="clear-selection">Limpiar</Button>
            )}
          </div>
        </div>
      )}

      <div className="sticky top-0 z-20 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-testid="inventory-toolbar-sticky">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nombre o código..."
          className="w-full sm:w-72 mb-2 px-3 py-2 rounded-full text-sm border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="inventory-search"
        />
        {categories.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1 [&_button]:shrink-0" data-testid="category-filter-bar">
            <button
              onClick={() => setFilterCategory('')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filterCategory === '' ? 'bg-primary text-white border-primary' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]'}`}
              data-testid="filter-cat-all"
            >
              Todas ({products.length})
            </button>
            {categories.map(c => {
              const count = products.filter(p => p.category_id === c.category_id).length;
              const active = filterCategory === c.category_id;
              return (
                <button
                  key={c.category_id}
                  onClick={() => setFilterCategory(c.category_id)}
                  style={catChipStyle(c.category_id, active)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                  data-testid={`filter-cat-${c.category_id}`}
                >
                  {c.name} ({count})
                </button>
              );
            })}
          </div>
        )}
      </div>

      {viewMode === 'list' ? (
        <div className="overflow-x-auto bg-white border border-[#E2E8F0] rounded-lg" data-testid="inventory-list-view">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] text-left text-[#64748B] text-xs uppercase">
                {isManager && <th className="px-2 py-2 w-8"></th>}
                <th className="px-3 py-2">Código</th>
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2 hidden sm:table-cell">Categoría</th>
                <th className="px-3 py-2 hidden md:table-cell">Almacén</th>
                <th className="px-3 py-2 text-right">Precio A</th>
                <th className="px-3 py-2 text-right hidden sm:table-cell">Precio B</th>
                <th className="px-3 py-2 text-right">Stock</th>
                {isManager && <th className="px-3 py-2 text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {visibleProducts.map((product) => {
                const isSel = selected.includes(product.product_id);
                return (
                  <tr key={product.product_id} className={`border-t border-[#F1F5F9] hover:bg-[#F8FAFC] ${isSel ? 'bg-primary/5' : ''}`} data-testid={`product-row-${product.product_id}`}>
                    {isManager && (
                      <td className="px-2 py-2">
                        <button onClick={() => toggleSelect(product.product_id)} data-testid={`select-product-${product.product_id}`}>
                          {isSel ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-[#94A3B8]" />}
                        </button>
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono text-xs text-primary">{product.code || '-'}</td>
                    <td className="px-3 py-2">
                      <button className="font-medium text-[#022C22] hover:underline text-left" onClick={() => setDetailProduct(product)}>{product.name}</button>
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell">
                      {catName_(product.category_id) ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border" style={catBadgeStyle(product.category_id)}>{catName_(product.category_id)}</span>
                      ) : <span className="text-[#94A3B8] text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-[#64748B]">{product.almacen || 'Almacén General'}</td>
                    <td className="px-3 py-2 text-right">${(product.price || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right hidden sm:table-cell text-primary">{(product.price_b != null) ? `$${Number(product.price_b).toFixed(2)}` : '-'}</td>
                    <td className="px-3 py-2 text-right">{product.item_type === 'servicio' ? 'N/A' : <span className={product.stock < 10 ? 'text-red-600 font-semibold' : ''}>{product.stock}</span>}</td>
                    {isManager && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <UnitsDialog product={product} onDone={loadProducts} /><Button size="icon" variant="ghost" onClick={() => openEdit(product)} data-testid={`edit-product-${product.product_id}`}><Edit size={14} /></Button>
                        <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDelete(product)} data-testid={`delete-product-${product.product_id}`}><Trash2 size={14} /></Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
        {visibleProducts.map((product) => {
          const isSel = selected.includes(product.product_id);
          return (
          <Card key={product.product_id} onClick={() => setDetailProduct(product)} className={`border shadow-sm overflow-hidden relative cursor-pointer hover:shadow-md transition-shadow ${isSel ? 'border-primary ring-2 ring-primary/30' : 'border-[#E2E8F0]'}`} data-testid={`product-card-${product.product_id}`}>
            {isManager && (
              <button
                onClick={(e) => { e.stopPropagation(); toggleSelect(product.product_id); }}
                className="absolute top-2 left-2 z-10 bg-white/90 rounded p-1 shadow"
                data-testid={`select-product-${product.product_id}`}
              >
                {isSel ? <CheckSquare size={20} className="text-primary" /> : <Square size={20} className="text-[#94A3B8]" />}
              </button>
            )}
            {product.photos && product.photos.length > 0 ? (
              <div className="aspect-video bg-[#F1F5F9] overflow-hidden">
                <img
                  src={product.photos[0].startsWith('http') ? product.photos[0] : api.fileUrl(product.photos[0])}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="aspect-video bg-[#F1F5F9] flex items-center justify-center">
                <Package size={40} className="text-[#CBD5E1]" />
              </div>
            )}
            <CardHeader>
              <CardTitle className="font-heading text-lg font-bold">{product.name}</CardTitle>
              {product.code && <p className="text-xs font-mono text-primary" data-testid={`product-code-${product.product_id}`}>Cód: {product.code}</p>}
              <p className="text-sm text-[#64748B]">{product.product_type} - {product.model}</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {product.item_type === 'servicio' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-teal-50 text-teal-700" data-testid={`badge-servicio-${product.product_id}`}>
                    <Wrench size={10} /> Servicio
                  </span>
                )}
                {catName_(product.category_id) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border" style={catBadgeStyle(product.category_id)} data-testid={`badge-cat-${product.product_id}`}>
                    <FolderTree size={10} /> {catName_(product.category_id)}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-[#64748B]">Precio A:</span>
                  <span className="font-semibold">${product.price.toFixed(2)}</span>
                </div>
                {product.price_b !== undefined && product.price_b !== null && product.price_b !== product.price && (
                  <div className="flex justify-between">
                    <span className="text-sm text-[#64748B]">Precio B:</span>
                    <span className="font-semibold text-primary">${Number(product.price_b).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-[#64748B]">Stock:</span>
                  {product.item_type === 'servicio' ? (
                    <span className="font-semibold text-teal-600">N/A</span>
                  ) : (
                    <span className={`font-semibold ${product.stock < 10 ? 'text-red-600' : 'text-green-600'}`}>
                      {product.stock}
                    </span>
                  )}
                </div>
              </div>
              {isManager && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-[#E2E8F0]" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(product)} data-testid={`edit-product-${product.product_id}`}>
                    <Edit size={14} className="mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(product)} data-testid={`delete-product-${product.product_id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          );
        })}
      </div>
      )}

      {/* Dialog Acciones masivas de precio */}
      {bulkOpen && (
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-md" data-testid="bulk-pricing-dialog">
            <DialogHeader>
              <DialogTitle>Acciones masivas de precio</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[#64748B]">{selected.length} producto(s) seleccionado(s)</p>
              <div>
                <Label>Tipo de ajuste</Label>
                <select
                  value={bulkForm.adjustment_type}
                  onChange={(e) => setBulkForm({ ...bulkForm, adjustment_type: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                  data-testid="bulk-adjustment-type"
                >
                  <option value="percent_increase">Aumentar %</option>
                  <option value="percent_decrease">Rebajar %</option>
                  <option value="fixed_increase">Aumentar monto ($)</option>
                  <option value="fixed_decrease">Rebajar monto ($)</option>
                  <option value="set_price">Fijar precio ($)</option>
                </select>
              </div>
              <div>
                <Label>Valor</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={bulkForm.value}
                  onChange={(e) => setBulkForm({ ...bulkForm, value: e.target.value })}
                  data-testid="bulk-value"
                  autoFocus
                />
                {previewLabel() && <p className="text-xs text-primary mt-1">{previewLabel()}</p>}
              </div>
              <div>
                <Label>Etiqueta (opcional)</Label>
                <Input
                  value={bulkForm.label}
                  onChange={(e) => setBulkForm({ ...bulkForm, label: e.target.value })}
                  placeholder="Oferta navideña, Black Friday..."
                  data-testid="bulk-label"
                />
              </div>
              <div>
                <Label>Motivo (opcional)</Label>
                <Input
                  value={bulkForm.reason}
                  onChange={(e) => setBulkForm({ ...bulkForm, reason: e.target.value })}
                  placeholder="Alza de costos, promoción..."
                  data-testid="bulk-reason"
                />
              </div>
              <Button onClick={applyBulk} className="w-full" data-testid="apply-bulk-pricing">
                <Tag size={16} className="mr-1.5" />
                Aplicar a {selected.length} producto(s)
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Ficha Técnica del Producto */}
      {detailProduct && (
        <Dialog open={!!detailProduct} onOpenChange={(o) => { if (!o) setDetailProduct(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {detailProduct.name}
                {detailProduct.item_type === 'servicio'
                  ? <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-teal-50 text-teal-700">Servicio</span>
                  : <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">Bien</span>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Galería de fotos */}
              {detailProduct.photos && detailProduct.photos.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {detailProduct.photos.map((ph, i) => (
                    <div key={i} className="aspect-square bg-[#F1F5F9] rounded-md overflow-hidden border border-[#E2E8F0]">
                      <img src={ph.startsWith('http') ? ph : api.fileUrl(ph)} alt={`foto-${i}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="aspect-video bg-[#F1F5F9] rounded-md flex items-center justify-center">
                  <Package size={40} className="text-[#CBD5E1]" />
                </div>
              )}

              {/* Datos generales */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#94A3B8] text-xs block">Tipo</span>{detailProduct.product_type || '-'}</div>
                <div><span className="text-[#94A3B8] text-xs block">Modelo</span>{detailProduct.model || '-'}</div>
                <div><span className="text-[#94A3B8] text-xs block">Categoría</span>{catName_(detailProduct.category_id) || 'Sin categoría'}</div>
                <div><span className="text-[#94A3B8] text-xs block">Almacén</span>{detailProduct.almacen || 'Almacén General'}</div>
                <div><span className="text-[#94A3B8] text-xs block">Ubicación</span>{detailProduct.location || '-'}</div>
                <div><span className="text-[#94A3B8] text-xs block">Precio</span><b className="text-base">${(detailProduct.price || 0).toFixed(2)}</b></div>
                <div><span className="text-[#94A3B8] text-xs block">Stock</span>{detailProduct.item_type === 'servicio' ? 'N/A' : detailProduct.stock}</div>
              </div>

              {/* Ficha técnica / características */}
              <div>
                <p className="text-xs font-semibold text-[#64748B] uppercase mb-2">Ficha técnica</p>
                {detailProduct.characteristics && Object.keys(detailProduct.characteristics).length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {Object.entries(detailProduct.characteristics).map(([k, v], i) => (
                          <tr key={k} className={i % 2 ? 'bg-[#F8FAFC]' : ''} data-testid={`char-detail-${k}`}>
                            <td className="px-3 py-2 font-medium text-[#475569] w-1/3 capitalize">{k}</td>
                            <td className="px-3 py-2 text-[#022C22]">{String(v)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-[#94A3B8]">Este artículo no tiene características registradas.</p>
                )}
              </div>

              {isManager && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button onClick={() => openEdit(detailProduct)} className="flex-1" data-testid="edit-product-button">
                    <Edit size={16} className="mr-1.5" /> Editar producto
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog Gestión de Categorías */}
      {catDialogOpen && (
        <Dialog open={catDialogOpen} onOpenChange={(o) => { setCatDialogOpen(o); if (!o) { setEditingCat(null); setCatName(''); } }}>
          <DialogContent className="max-w-md" data-testid="categories-dialog">
            <DialogHeader>
              <DialogTitle>Categorías de productos</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={catName}
                  onChange={(e) => setCatName(e.target.value)}
                  placeholder={editingCat ? 'Editar nombre...' : 'Nueva categoría...'}
                  data-testid="category-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveCategory(); } }}
                />
                <Button onClick={saveCategory} data-testid="save-category-button">
                  {editingCat ? 'Guardar' : 'Crear'}
                </Button>
                {editingCat && (
                  <Button variant="ghost" size="icon" onClick={() => { setEditingCat(null); setCatName(''); }} data-testid="cancel-edit-category">
                    <X size={16} />
                  </Button>
                )}
              </div>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {categories.length === 0 && (
                  <p className="text-sm text-center text-[#94A3B8] py-4">Aún no hay categorías</p>
                )}
                {categories.map(c => (
                  <div key={c.category_id} className="flex items-center justify-between px-3 py-2 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]" data-testid={`category-item-${c.category_id}`}>
                    <span className="text-sm font-medium flex items-center gap-2">
                      <FolderTree size={14} className="text-indigo-500" /> {c.name}
                    </span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => { setEditingCat(c); setCatName(c.name); }} data-testid={`edit-category-${c.category_id}`}>
                        <Edit size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteCategory(c)} data-testid={`delete-category-${c.category_id}`}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog Gestión de Almacenes */}
      {whDialogOpen && (
        <Dialog open={whDialogOpen} onOpenChange={(o) => { setWhDialogOpen(o); if (!o) { setEditingWh(null); setWhName(''); } }}>
          <DialogContent className="max-w-md" data-testid="warehouses-dialog">
            <DialogHeader>
              <DialogTitle>Almacenes / Localidades</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-[#64748B]">Los productos pertenecen a un almacén. El "Almacén General" es el principal; puedes crear localidades adicionales (sucursales, depósitos) para organizar el inventario de esta empresa.</p>
              <div className="flex gap-2">
                <Input
                  value={whName}
                  onChange={(e) => setWhName(e.target.value)}
                  placeholder={editingWh ? 'Editar nombre...' : 'Nuevo almacén...'}
                  data-testid="warehouse-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveWarehouse(); } }}
                />
                <Button onClick={saveWarehouse} data-testid="save-warehouse-button">
                  {editingWh ? 'Guardar' : 'Crear'}
                </Button>
                {editingWh && (
                  <Button variant="ghost" size="icon" onClick={() => { setEditingWh(null); setWhName(''); }} data-testid="cancel-edit-warehouse">
                    <X size={16} />
                  </Button>
                )}
              </div>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {warehouses.length === 0 && (
                  <p className="text-sm text-center text-[#94A3B8] py-4">Aún no hay almacenes</p>
                )}
                {warehouses.map(w => (
                  <div key={w.warehouse_id} className="flex items-center justify-between px-3 py-2 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]" data-testid={`warehouse-item-${w.warehouse_id}`}>
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Warehouse size={14} className="text-primary" /> {w.name}
                      {w.is_default && <span className="px-2 py-0.5 rounded-full text-[10px] bg-primary/10 text-primary">Principal</span>}
                    </span>
                    {!w.is_default && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setEditingWh(w); setWhName(w.name); }} data-testid={`edit-warehouse-${w.warehouse_id}`}>
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteWarehouse(w)} data-testid={`delete-warehouse-${w.warehouse_id}`}>
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog Mover productos (categoría / almacén) */}
      {moveOpen && (
        <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
          <DialogContent className="max-w-md" data-testid="bulk-move-dialog">
            <DialogHeader>
              <DialogTitle>Mover {selected.length} producto(s)</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button size="sm" variant={moveForm.mode === 'category' ? 'default' : 'outline'} onClick={() => setMoveForm({ ...moveForm, mode: 'category' })} data-testid="move-mode-category">
                  <FolderTree size={14} className="mr-1.5" /> A categoría
                </Button>
                <Button size="sm" variant={moveForm.mode === 'warehouse' ? 'default' : 'outline'} onClick={() => setMoveForm({ ...moveForm, mode: 'warehouse' })} data-testid="move-mode-warehouse">
                  <Warehouse size={14} className="mr-1.5" /> A almacén
                </Button>
              </div>
              {moveForm.mode === 'category' ? (
                <div>
                  <Label>Categoría destino</Label>
                  <select value={moveForm.category_id} onChange={(e) => setMoveForm({ ...moveForm, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10" data-testid="move-category-select">
                    <option value="">Sin categoría</option>
                    {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <Label>Almacén destino</Label>
                  <select value={moveForm.almacen} onChange={(e) => setMoveForm({ ...moveForm, almacen: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10" data-testid="move-warehouse-select">
                    <option value="">Selecciona un almacén...</option>
                    {warehouses.map(w => <option key={w.warehouse_id} value={w.name}>{w.name}</option>)}
                  </select>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setMoveOpen(false)} data-testid="move-cancel-button">Cancelar</Button>
                <Button className="flex-1" onClick={applyMove} data-testid="move-confirm-button">Mover</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {products.length === 0 && (
        <div className="text-center py-12" data-testid="no-products-message">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No hay productos registrados</p>
        </div>
      )}

      {/* Confirmación de carga masiva */}
      {bulkPreview && (
        <Dialog open={!!bulkPreview} onOpenChange={(o) => { if (!o) setBulkPreview(null); }}>
          <DialogContent className="max-w-md" data-testid="bulk-upload-confirm-dialog">
            <DialogHeader>
              <DialogTitle>SalesPRO · Confirmar carga masiva</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[#475569]">
                Estás a punto de cargar <b className="text-primary" data-testid="bulk-valid-count">{bulkPreview.valid_count}</b> artículo(s) a la empresa{' '}
                <b className="text-[#022C22]" data-testid="bulk-target-company">«{bulkPreview.company_name}»</b>.
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-lg bg-[#ECFDF5] border border-[#A7F3D0]">
                  <p className="text-[10px] uppercase text-[#64748B]">Válidos</p>
                  <p className="font-bold text-primary">{bulkPreview.valid_count}</p>
                </div>
                <div className="p-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                  <p className="text-[10px] uppercase text-[#64748B]">Filas totales</p>
                  <p className="font-bold text-[#022C22]">{bulkPreview.total_rows}</p>
                </div>
                <div className={`p-2 rounded-lg border ${bulkPreview.error_count > 0 ? 'bg-red-50 border-red-200' : 'bg-[#F8FAFC] border-[#E2E8F0]'}`}>
                  <p className="text-[10px] uppercase text-[#64748B]">Con error</p>
                  <p className={`font-bold ${bulkPreview.error_count > 0 ? 'text-red-600' : 'text-[#022C22]'}`}>{bulkPreview.error_count}</p>
                </div>
              </div>
              {bulkPreview.error_count > 0 && (
                <div className="max-h-32 overflow-y-auto text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 space-y-0.5">
                  {bulkPreview.errors.map((er, i) => (
                    <p key={i}>Fila {er.row}: {er.error}</p>
                  ))}
                  <p className="text-[#64748B] pt-1">Las filas con error se omitirán; el resto se cargará.</p>
                </div>
              )}
              {((bulkPreview.new_categories && bulkPreview.new_categories.length > 0) || (bulkPreview.new_warehouses && bulkPreview.new_warehouses.length > 0)) && (
                <div className="text-xs text-[#475569] bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-2 space-y-0.5">
                  {bulkPreview.new_categories && bulkPreview.new_categories.length > 0 && (
                    <p>Se crearán {bulkPreview.new_categories.length} categoría(s) nueva(s): {bulkPreview.new_categories.join(', ')}</p>
                  )}
                  {bulkPreview.new_warehouses && bulkPreview.new_warehouses.length > 0 && (
                    <p>Se crearán {bulkPreview.new_warehouses.length} almacén(es) nuevo(s): {bulkPreview.new_warehouses.join(', ')}</p>
                  )}
                </div>
              )}
              <p className="text-xs text-[#94A3B8]">Verifica que la empresa destino sea la correcta antes de confirmar.</p>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setBulkPreview(null)} disabled={bulkUploading} data-testid="bulk-cancel-button">
                  Cancelar
                </Button>
                <Button className="flex-1" onClick={confirmBulkUpload} disabled={bulkUploading || bulkPreview.valid_count === 0} data-testid="bulk-confirm-button">
                  {bulkUploading ? 'Cargando...' : `Confirmar y cargar ${bulkPreview.valid_count}`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}